import { HttpResponse } from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';
import { List, Map } from 'immutable';
import {
  interval as observableInterval,
  of as observableOf,
  Observable,
} from 'rxjs';
import { catchError } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { CurrentUserService } from '®shared/current-user';
import { TarlV3Api } from '®shared/tarl-v3-api';

import { AnalyticsEvent } from './analytics-event';
import { EventSources } from './event-sources';
import {
  EVENT_SEND_INTERVAL_MS,
  EVENT_SERVICE_MAX_BATCH_SIZE,
  EVENT_SERVICE_MAX_RETRY_COUNT,
  EVENT_SERVICE_SEND_ENABLED,
} from './events-config.constant';
import { isStringifiable } from './is-stringifiable';

interface EventWithSourceAndTimestamp extends AnalyticsEvent {
  source: string;
  timestamp: string;
}

interface EchoEvent {
  class: string;
  source: string;
  timestamp: string;
  user: string | null;
  props: { [key: string]: any } & { client_session_id: string };
}

interface LocalEventAndRetryCount {
  event: EventWithSourceAndTimestamp;
  /** @type {string} A local-only ID, used only to identify events in client storage */
  localId: string;
  retryCount: number;
}

@Injectable()
export class EventsService {
  private unsentEventsByLocalId = Map<string, LocalEventAndRetryCount>();
  private isLocked = false;

  constructor(
    private currentUserService: CurrentUserService,
    @Inject(EVENT_SEND_INTERVAL_MS) eventSendIntervalMs: number,
    @Inject(EVENT_SERVICE_MAX_RETRY_COUNT) private maxRetryCount: number,
    @Inject(EVENT_SERVICE_MAX_BATCH_SIZE) private maxBatchSize: number,
    @Inject(EVENT_SERVICE_SEND_ENABLED) isEventServiceSendEnabled: boolean,
    private ngZone: NgZone,
    private tarlV3Api: TarlV3Api,
  ) {
    if (isEventServiceSendEnabled) {
      this.startEventCollectorOnInterval(eventSendIntervalMs);
    }
  }

  startEventCollectorOnInterval(intervalMs: number) {
    /**
     * We run this outside Angular because the e2e tests need to detect that the app
     * has "settled", and this is done by checking what's running in the Angular zone.
     * Without this, e2e tests never think the app is loaded.
     * @see {@link https://github.com/angular/protractor/blob/master/docs/timeouts.md#waiting-for-angular}
     */
    this.ngZone.runOutsideAngular(() => {
      observableInterval(intervalMs).forEach(_interval => {
        this.collectBatchAndSend();
      });
    });
  }

  recordEvent(
    event: AnalyticsEvent,
    source: string = EventSources.DEFAULT,
  ): void {
    if (!isStringifiable(event.props)) {
      return;
    }
    this.queueEventForSend(event, source);
  }

  private getSessionId(): string {
    return this.currentUserService.getSessionId();
  }

  private queueEventForSend(event: AnalyticsEvent, source: string): void {
    if (!event) {
      return;
    }
    const localId = uuid();
    const timestamp = this.getTimestamp();
    this.unsentEventsByLocalId = this.unsentEventsByLocalId.set(localId, {
      event: { ...event, source, timestamp },
      localId,
      retryCount: 0,
    });
  }

  /**
   * Timestamps are strings, containing an integer representing seconds since the Unix epoch.
   *
   * @return {string} A timestamp, seconds since the epoch
   */
  getTimestamp(): string {
    return Math.round(new Date().getTime() / 1000).toString();
  }

  private get user(): string | null {
    return this.currentUserService.getCurrentUserGuid();
  }

  collectBatchAndSend(): void {
    if (this.isLocked) {
      return;
    }

    const batchOfLocalEvents = this.unsentEventsByLocalId
      .toList()
      .take(this.maxBatchSize);

    // nothing to send, so return immediately
    if (batchOfLocalEvents.isEmpty()) {
      return;
    }

    const batchOfRealEvents = batchOfLocalEvents.map(({ event }) => event);
    const localIdsInBatch = batchOfLocalEvents.map(({ localId }) => localId);

    this.isLocked = true;

    this.sendBatchOfEvents(batchOfRealEvents)
      .pipe(
        catchError(error => {
          if (error && error.status >= 400 && error.status < 600) {
            // Only increment retryCount if a server error response was received.
            // This allows for offline responses (i.e. tunnels) to be retried indefinitely
            this.incrementRetryCountForUnsentEvents(localIdsInBatch);
          }
          this.isLocked = false;

          return observableOf(null);
        }),
      )
      .forEach(response => {
        if (response && response.ok) {
          this.removeSentEvents(localIdsInBatch);
        }
        this.isLocked = false;
      });
  }

  private removeSentEvents(batchOfLocalIds: List<string>): void {
    this.unsentEventsByLocalId = this.unsentEventsByLocalId.deleteAll(
      batchOfLocalIds,
    );
  }

  private incrementRetryCountForUnsentEvents(
    batchOfLocalIds: List<string>,
  ): void {
    this.unsentEventsByLocalId = batchOfLocalIds
      .reduce(
        (unsentEventsByLocalIdSoFar, localId) =>
          unsentEventsByLocalIdSoFar.update(localId, localEvent => ({
            ...localEvent,
            retryCount: localEvent.retryCount + 1,
          })),
        this.unsentEventsByLocalId,
      )
      .filter(localEvent => localEvent.retryCount < this.maxRetryCount);
  }

  private convertToEchoEvent(event: EventWithSourceAndTimestamp): EchoEvent {
    const { timestamp, props = {}, className, source } = event;

    return {
      class: className,
      source,
      timestamp,
      user: this.user,
      props: { ...props, client_session_id: this.getSessionId() },
    };
  }

  private sendBatchOfEvents(
    eventData: List<EventWithSourceAndTimestamp>,
  ): Observable<HttpResponse<Object> | null> {
    if (!eventData || eventData.isEmpty()) {
      return observableOf(null);
    }

    const path = this.tarlV3Api.path('/events', {});
    const requestBody = eventData
      .map(event => this.convertToEchoEvent(event))
      .toArray();
    return this.tarlV3Api.post(path, requestBody);
  }
}
